import { useEffect, useState } from 'react';
import MainApp from './components/MainApp';
import { AuthProvider } from './context/authContext';
import { RoleProvider } from './context/roleContext';
import { ErrorDialogProvider } from './context/errorDialogContext';
import { MessageAlertProvider } from './components/common/MessageAlert';
import { WarehouseProvider } from './context/WarehouseContext';
import { PortalAuthProvider } from './context/portalAuthContext';
import CustomerPortalApp from './portal/CustomerPortalApp';
import { getSurface } from './utils/portalStorage';

/** The staff ERP app (existing) — unchanged provider tree. */
function StaffApp() {
  return (
    <AuthProvider>
      <RoleProvider>
        <ErrorDialogProvider>
          <MessageAlertProvider>
            <WarehouseProvider>
              <MainApp />
            </WarehouseProvider>
          </MessageAlertProvider>
        </ErrorDialogProvider>
      </RoleProvider>
    </AuthProvider>
  );
}

/**
 * Top-level surface switch. The app has no router; instead we render EITHER the
 * staff app OR the customer portal based on `app_surface` in localStorage
 * (toggled by the login tabs / portal logout). The two never share providers or
 * tokens, so a staff session and a portal session are fully isolated.
 */
function App() {
  const [surface, setSurfaceState] = useState(() => getSurface());

  useEffect(() => {
    const sync = () => setSurfaceState(getSurface());
    // Same-tab switches fire a custom event; other tabs fire native 'storage'.
    window.addEventListener('app-surface-change', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('app-surface-change', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  if (surface === 'portal') {
    return (
      <MessageAlertProvider>
        <PortalAuthProvider>
          <CustomerPortalApp />
        </PortalAuthProvider>
      </MessageAlertProvider>
    );
  }

  return <StaffApp />;
}

export default App;
